/*
  A Light DOM component wrapping a text input

  * Implements expandable behavior on focus to save space.
*/
import classes from './_search_field.module.scss';

/**
 * Class implements a wrapper for a text input.
 * A search icon and a label are automatically generated.
 */
export default class UaSearchField extends HTMLElement {
  wrapper;
  inputElement;
  labelElement;
  iconElement;
  static observedAttributes = ['input-label'];

  /**
   * input-label property reflects attribute
   *
   * @return {string} - the input-label attribute
   */
  get inputLabel() {
    return this.getAttribute('input-label');
  }

  /**
   * Label setter: input-abel property reflects to attribute.
   *
   * @param {string} value - the new label.
   * */
  set inputLabel(value) {
    this.setAttribute('input-label', value);
  }

  /** Check for problems */
  #validate() {
    if (!this.inputElement || this.inputElement.getAttribute('type') !== 'text') {
      throw new Error('UaSearchField Error: Must enclose an input[type="text"].');
    }
    if (this.inputElement.id === '') {
      throw new Error('UaSearchField Error: Enclosed input must have an id.');
    }
  }

  /** Create supporting elements and attach classes and attributes to elements */
  #decorate() {
    this.classList.add(classes['c-search-field']);
    this.inputElement.classList.add(classes['c-search-field__input']);
    this.labelElement = document.createElement('label');
    this.labelElement.setAttribute('for', this.inputElement.id);
    this.labelElement.classList.add(classes['c-search-field__label']);
    this.iconElement = document.createElement('music-icon-search-v3');
    this.iconElement.classList.add(classes['c-search-field__icon']);
  }

  /** Create the hierarchy for the supplied and generated elements */
  #addWrapper() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add(classes['c-search-field__inner'], classes['can-expand-left']);
    this.labelElement.appendChild(this.iconElement);
    this.wrapper.appendChild(this.labelElement);
    this.wrapper.appendChild(this.inputElement);
    this.appendChild(this.wrapper);
  }

  /** Set up event listeners */
  #addEvents() {

    // Add/remove is-focused class
    this.inputElement.addEventListener('focus', () => {
      this.inputElement.classList.add('is-focused');
    });

    this.inputElement.addEventListener('blur', () => {
      this.inputElement.classList.remove('is-focused');
    });

    // Collapse when Escape key pressed
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        this.inputElement.blur();
      }
    });
  }

  /** Initialization */
  connectedCallback() {
    if (!this.wrapper) {
      this.inputElement = this.querySelector('input');
      this.#validate();
      this.#decorate();
      this.#addWrapper();
      this.#addEvents();
    }
  }

  /** Update the component when an attribute changes */
  attributeChangedCallback(name, oldValue, newValue) {
    const input = this.querySelector('input');
    if (input && name === 'input-label' && newValue.length > 0) {
      input.setAttribute('aria-label', newValue);
    }
  }
}
